import {none, some} from '@danwang/option';
import {
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
} from '@transactions/graphql';
import {CornerDownLeft, Sparkles} from 'lucide-react';
import * as React from 'react';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import Loading from 'src/Loading';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import {useSearchPopover} from 'src/SearchPopover/useSearchPopover';
import {useSearch} from 'src/useSearch';
import type {Option} from '@danwang/option';
import type {FocusControls} from 'src/FocusTrap';
import type {SubmitState} from 'src/SearchPopover/useSearchPopover';

// A category from the raw selection's categorization history, pinned above
// the regular options under a "Suggested" section. `detail` is the
// provenance shown on the row (e.g. "9 of 14").
export type CategorySuggestion = {
  name: string;
  detail: string;
};

type Props = {
  selectedCategory: Option<string>;
  onSelectCategory: (category: Option<string>) => void;
  // Pinned above the regular options — for the empty query and whenever they
  // match the typed query — so historical matches are one keystroke away.
  suggestions?: CategorySuggestion[];
  // Rendered inside the input's right edge (e.g. auto-apply provenance).
  adornment?: React.ReactNode;
  inputClassName?: string;
  // Fired on the user's first keystroke — before the text commits into
  // the selection on submit/blur — so a caller can stop a late suggestion
  // response from overwriting in-progress input.
  onEdited?: () => void;
};

const CategoryPicker = (props: Props) => {
  const {suggestions, adornment, inputClassName, onEdited} = props;
  const {loading, data} = useAllCategoriesQuery();
  const {data: groupsData} = useAllCategoryGroupsQuery();
  const {search} = useSearch(data?.allCategories || [], ['name']);

  // Browse order for the empty query: groups in display order (members in
  // member order), then ungrouped categories in name order. sectionByName
  // drives the section labels rendered between items; boundaries compare
  // by group ID, not label — names are not unique, so two adjacent groups
  // with the same name (or one literally named "Ungrouped") must still
  // render as separate sections.
  const {browseOrder, sectionByName} = React.useMemo(() => {
    const categories = data?.allCategories || [];
    const groups = groupsData?.allCategoryGroups || [];
    const byId = new Map(categories.map((c) => [c._id, c]));
    const grouped = new Set(groups.flatMap((g) => g.categoryIds));

    const order: string[] = [];
    const sections = new Map<string, {key: string; label: string}>();
    for (const group of groups) {
      for (const id of group.categoryIds) {
        const category = byId.get(id);
        if (category) {
          order.push(category.name);
          sections.set(category.name, {key: group._id, label: group.name});
        }
      }
    }
    for (const category of categories) {
      if (!grouped.has(category._id)) {
        order.push(category.name);
        if (groups.length > 0) {
          sections.set(category.name, {
            key: '__ungrouped__',
            label: 'Ungrouped',
          });
        }
      }
    }
    return {browseOrder: order, sectionByName: sections};
  }, [data, groupsData]);

  type CategoryOption =
    | string
    | {type: 'create'; value: string}
    | {type: 'suggested'; value: string; detail: string};

  const optionToString = (option: CategoryOption): string =>
    typeof option === 'string' ? option : option.value;

  const optionKey = (option: CategoryOption): string =>
    typeof option === 'string'
      ? `category:${option}`
      : `${option.type}:${option.value}`;

  const renderOption = (option: CategoryOption) => {
    if (typeof option === 'string') {
      return option;
    } else if (option.type === 'suggested') {
      return (
        <div className="flex w-full min-w-0 items-center gap-2">
          <Sparkles className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{option.value}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {option.detail}
          </span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2">
          <CornerDownLeft className="size-4" />
          Add "{option.value}"
        </div>
      );
    }
  };

  // The section an option renders under while browsing. Suggested options get
  // their own section; when suggestions are present but no groups exist, the
  // plain list still gets a label so the boundary below the suggestions reads
  // as a section break rather than a run-on.
  const sectionOf = (
    option: CategoryOption,
  ): {key: string; label: string} | undefined => {
    if (typeof option !== 'string') {
      return option.type === 'suggested'
        ? {key: '__suggested__', label: 'Suggested'}
        : undefined;
    }
    const section = sectionByName.get(option);
    if (section) return section;
    return (suggestions ?? []).length > 0
      ? {key: '__all__', label: 'All categories'}
      : undefined;
  };

  const anchorRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const activeItemRef = React.useRef<HTMLDivElement | null>(null);
  const focusControlsRef = React.useRef<FocusControls | null>(null);
  // Submissions move focus out of the FocusTrap, which fires onFocusOutside
  // synchronously before the popover unmounts; this flag stops that from
  // re-committing the raw query over the just-committed selection
  const justSubmittedRef = React.useRef(false);

  // Browsing = nothing typed beyond the current selection: the empty query,
  // or exactly the already-selected value (focusing a filled picker). An
  // auto-applied category would otherwise filter the dropdown to itself and
  // hide the alternative suggestions the picker exists to surface.
  const isBrowsing = (query: string): boolean => {
    const trimmed = query.trim();
    return trimmed === '' || trimmed === props.selectedCategory.getOrElse('');
  };

  const handleSearch = (query: string): CategoryOption[] => {
    if (loading) return [];

    const trimmed = query.trim();
    const toOption = (s: CategorySuggestion): CategoryOption => ({
      type: 'suggested',
      value: s.name,
      detail: s.detail,
    });

    // Browsing pins every suggestion (unfiltered — the query is just the
    // current selection) above the grouped list; a suggested category also
    // stays visible in its own group. Typed queries keep the flat relevance
    // ranking with matching suggestions bumped to the top and deduplicated.
    if (isBrowsing(query)) {
      return [...(suggestions ?? []).map(toOption), ...browseOrder];
    }

    const q = trimmed.toLowerCase();
    const suggested = (suggestions ?? [])
      .filter((s) => s.name.toLowerCase().includes(q))
      .map(toOption);
    const suggestedNames = new Set(suggested.map(optionToString));
    const results = search(query, {limit: 80})
      .map(({name}) => name)
      .filter((name) => !suggestedNames.has(name));
    return results.includes(trimmed) || suggestedNames.has(trimmed)
      ? [...suggested, ...results]
      : [...suggested, ...results, {type: 'create', value: trimmed}];
  };

  const handleSubmit = (state: SubmitState<CategoryOption>) => {
    // Picking an option (click, or arrow/Tab selection) is a manual choice
    // just like typing, so it must also drop auto-apply provenance — while
    // a blur/tab-through commit of unchanged text (selection null) is not
    // an edit and must leave it alone.
    if (state.selection !== null) {
      onEdited?.();
    }
    const nextValue = state.selection
      ? optionToString(state.selection)
      : state.query;
    const trimmed = nextValue.trim();

    if (trimmed === '') {
      props.onSelectCategory(none);
    } else {
      props.onSelectCategory(some(trimmed));
    }
    closePopover();
    justSubmittedRef.current = true;
    if (state.focusDirection === 'backward') {
      focusControlsRef.current?.focusBackward();
    } else {
      focusControlsRef.current?.focusForward();
    }
  };

  const {
    isOpen,
    query,
    searchResults,
    selectedIndex,
    handleKeyDown,
    handleQueryChange,
    openPopover,
    closePopover,
    clearSelection,
  } = useSearchPopover<CategoryOption>({
    initialQuery: props.selectedCategory.getOrElse(''),
    focusControlsRef,
    search: handleSearch,
    onSubmit: handleSubmit,
    // While browsing, Tab must commit the current query, not the browse
    // list's first item — auto-selecting there would silently replace a
    // filled category on tab-through.
    autoSubmitFirstOnTab: (query) => !isBrowsing(query),
  });

  React.useEffect(() => {
    if (isOpen) {
      justSubmittedRef.current = false;
    }
  }, [isOpen]);

  // Suggestions arrive asynchronously and prepend rows without changing the
  // query — and on a cold cache the groups query can land after the
  // categories query, rebuilding browseOrder under an open popover. Either
  // way an index-based keyboard highlight would silently move to a
  // different option, so drop it when the result ordering changes.
  React.useEffect(() => {
    clearSelection();
  }, [suggestions, browseOrder, clearSelection]);

  // Commit typed text when focus leaves the picker (outside click/focus),
  // matching the Enter/Tab behavior — otherwise text typed without an
  // explicit selection is silently dropped
  const commitQuery = () => {
    if (justSubmittedRef.current) {
      justSubmittedRef.current = false;
      closePopover();
      return;
    }
    const trimmed = query.trim();
    if (trimmed === '') {
      props.onSelectCategory(none);
    } else {
      props.onSelectCategory(some(trimmed));
    }
    closePopover();
  };

  React.useLayoutEffect(() => {
    if (isOpen && anchorRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${anchorRef.current.offsetWidth}px`;
    }
  }, [anchorRef.current, popoverRef.current, isOpen]);

  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  return (
    <>
      <div className="relative">
        <TextInput
          ref={anchorRef}
          value={query}
          onChange={(next) => {
            onEdited?.();
            handleQueryChange(next);
          }}
          onClick={openPopover}
          onFocus={openPopover}
          onKeyDown={handleKeyDown}
          placeholder="Category"
          className={cn(adornment != null && 'pr-20', inputClassName)}
        />
        {adornment != null && (
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {adornment}
          </div>
        )}
      </div>
      <Popover
        anchorRef={anchorRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={isOpen}
        onOutsideClick={commitQuery}
        onOutsideFocus={commitQuery}
        passThroughOutsideClicks
      >
        <PopoverContents ref={popoverRef}>
          {loading ? (
            <Loading />
          ) : (
            searchResults.map((result, index) => {
              const browsing = isBrowsing(query);
              const section = browsing ? sectionOf(result) : undefined;
              const previousSection =
                browsing && index > 0
                  ? sectionOf(searchResults[index - 1])
                  : undefined;
              const showLabel =
                section != null && section.key !== previousSection?.key;
              return (
                <React.Fragment key={optionKey(result)}>
                  {showLabel && (
                    <div className="text-muted-foreground px-3 pb-1 pt-2 text-xs font-semibold">
                      {section.label}
                    </div>
                  )}
                  <PopoverItem
                    isSelected={selectedIndex === index}
                    ref={selectedIndex === index ? activeItemRef : null}
                    onClick={() => handleSubmit({query, selection: result})}
                  >
                    {renderOption(result)}
                  </PopoverItem>
                </React.Fragment>
              );
            })
          )}
        </PopoverContents>
      </Popover>
    </>
  );
};

export default CategoryPicker;
